/**
 * useWebCanvasColor — keeps the page canvas in step with the active theme on web.
 *
 * On mobile Safari the strips behind the status bar and behind the toolbar are
 * painted with the page CANVAS — the root element's background — not with
 * anything the React tree renders. `theme-color` is ignored there and
 * `viewport-fit=cover` does not extend the page, so the canvas is the only
 * lever (measured on an iOS 26.1 simulator with five one-variable pages: only
 * the canvas color moved the strips). public/index.html gives the canvas an
 * OS-color-scheme default before the app mounts; once the theme is known this
 * writes the active base surface onto the root element — an inline style, so
 * it wins over that default — and onto every theme-color meta, for browsers
 * that still tint their chrome from it.
 *
 * A layout effect, so the canvas and the screens change in the same frame when
 * the theme is toggled. No-op on native and wherever there is no document.
 */
import { useLayoutEffect } from 'react';
import { Platform } from 'react-native';

// The app's tsconfig has no DOM lib: the browser global is reached the way the
// rest of the web-facing code does (see utils/common/alert.ts), and these
// are the slices of the DOM the hook touches.
declare const document: any;
type DocumentLike = {
  documentElement: { style: { backgroundColor: string } };
  querySelectorAll: (selector: string) => ArrayLike<{ content: string }>;
};

export function useWebCanvasColor(color: string): void {
  useLayoutEffect(() => {
    if (Platform.OS !== 'web') return;
    const doc = typeof document === 'undefined' ? undefined : (document as DocumentLike);
    if (!doc) return;
    doc.documentElement.style.backgroundColor = color;
    const metas = doc.querySelectorAll('meta[name="theme-color"]');
    for (let i = 0; i < metas.length; i += 1) metas[i].content = color;
  }, [color]);
}
